import React from "react";
import PropTypes from "prop-types";
import { Table, Badge } from "react-bootstrap";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import { useSelector } from "react-redux";

const getRateBadge = (rate) => {
  const numericRate = parseFloat(rate.replace("%", ""));

  if (numericRate >= 70) {
    return (
      <Badge bg="danger" pill>
        {rate}
      </Badge>
    );
  } else if (numericRate >= 60) {
    return (
      <Badge bg="warning" pill>
        {rate}
      </Badge>
    );
  } else if (numericRate >= 50) {
    return (
      <Badge bg="success" pill>
        {rate}
      </Badge>
    );
  }
  return (
    <Badge bg="secondary" pill>
      {rate}
    </Badge>
  );
};

const DepartmentRow = ({ dept, isLast }) => {
  return (
    <tr>
      <td
        className={`align-middle fw-medium ${isLast ? "border-bottom-0" : ""}`}
      >
        {dept.department}
      </td>
      <td
        className={`align-middle text-center ${isLast ? "border-bottom-0" : ""
          }`}
      >
        {dept.totalClaims.toLocaleString()}
      </td>
      <td
        className={`align-middle text-center text-danger ${isLast ? "border-bottom-0" : ""
          }`}
      >
        {dept.rejectedClaims.toLocaleString()}
      </td>
      <td
        className={`align-middle text-center ${isLast ? "border-bottom-0" : ""
          }`}
      >
        {getRateBadge(dept.rejectionRate)}
      </td>
      <td
        className={`align-middle text-center ${isLast ? "border-bottom-0" : ""
          }`}
      >
        {dept.avgClaimValue.toLocaleString()} SR
      </td>
      <td
        className={`align-middle text-center text-danger fw-medium ${isLast ? "border-bottom-0" : ""
          }`}
      >
        {dept.financialLoss.toLocaleString()} SR
      </td>
    </tr>
  );
};

DepartmentRow.propTypes = {
  dept: PropTypes.shape({
    department: PropTypes.string.isRequired,
    totalClaims: PropTypes.number.isRequired,
    rejectedClaims: PropTypes.number.isRequired,
    rejectionRate: PropTypes.string.isRequired,
    avgClaimValue: PropTypes.number.isRequired,
    financialLoss: PropTypes.number.isRequired,
  }),
  isLast: PropTypes.bool.isRequired,
};

const DepartmentPerformanceSummary = ({ data }) => {
  return (
    <>
      <h6 className="fs-9 mt-3 mb-0 fw-normal text-primary">Rejection Per Specialty Overview</h6>
      <SimpleBar>
        <Table className="fs-10 mb-0" bordered hover responsive>
          <thead className="bg-200">
            <tr>
              <th className="text-800 text-nowrap">Department</th>
              <th className="text-800 text-nowrap text-center">Total Claims</th>
              <th className="text-800 text-nowrap text-center">
                Rejected Claims
              </th>
              <th className="text-800 text-nowrap text-center">Rejection Rate</th>
              <th className="text-800 text-nowrap text-center">
                Avg Claim Value
              </th>
              <th className="text-800 text-nowrap text-center">Financial Loss</th>
            </tr>
          </thead>
          <tbody>
            {data.map((dept, index) => (
              <DepartmentRow
                key={dept.department}
                dept={dept}
                isLast={index === data.length - 1}
              />
            ))}
          </tbody>
        </Table>
      </SimpleBar>
    </>
  );
};

DepartmentPerformanceSummary.propTypes = {
  data: PropTypes.arrayOf(DepartmentRow.propTypes.dept).isRequired,
};

export default function DepartmentPerformanceSummaryTable() {
  const { claimDashboardData } = useSelector((state) => state.claimDashboard);
  const claimDept = claimDashboardData?.claim_dept || [];

  // Map claim_dept to table data
  const departments = claimDept.map((dept) => ({
    department: dept.department,
    totalClaims: dept.total_claim || 0,
    rejectedClaims: dept.total_rejclaim || 0,
    rejectionRate: dept.total_claim
      ? ((dept.total_rejclaim / dept.total_claim) * 100).toFixed(1) + "%"
      : "0.0%",
    avgClaimValue: dept.total_claim
      ? Math.round(dept.total_submtamnt / dept.total_claim)
      : 0,
    financialLoss: dept.total_rejsubmtamnt || 0,
  }));

  return <DepartmentPerformanceSummary data={departments} />;
}